<template>
  <DialogLayout class-name="ai-recommend-reason-dialog" @close="emit('close')">
    <template #title>{{ t('jobMatch.aiReasonDialog.title') }}</template>
    <el-form ref="formRef" v-loading="loading" :model="formData" :rules="rules" label-position="top"
      class="ai-reason-form">
      <el-form-item :label="t('jobMatch.aiReasonDialog.meeting')" prop="bookingId">
        <el-select v-model="formData.bookingId" :placeholder="t('common.pleaseSelect')">
          <el-option v-for="option in meetingOptions" :key="option.bookingId" :label="formatMeeting(option)"
            :value="option.bookingId" />
        </el-select>
        <div class="ai-reason-tip">{{ t('jobMatch.aiReasonDialog.tip') }}</div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button class="confirm-button-default" @click="emit('cancel')">{{ t('common.cancel') }}</el-button>
      <el-button type="primary" @click="handleConfirm">{{ t('jobMatch.aiReasonDialog.generate') }}</el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { FormInstance, FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import { getRecommendReasonInterviewsApi } from '@/api/job-match'
import { CounselingMode, type CounselingBooking } from '@/api/interview'

// AI 自动生成推荐理由：选择一条面谈纪要（默认最近一次），确认后把 bookingId 交给调用方生成
const props = defineProps<{
  candidateId: number | string
  jobId: number
}>()

const emit = defineEmits<{
  confirm: [bookingId: number]
  cancel: []
  close: []
}>()

const { t } = useI18n()
const formRef = ref<FormInstance>()
const loading = ref(false)
const meetingOptions = ref<CounselingBooking[]>([])
const formData = reactive({ bookingId: undefined as number | undefined })
const rules: FormRules = {
  bookingId: [{ required: true, message: t('jobMatch.aiReasonDialog.pleaseSelectMeeting'), trigger: 'change' }],
}

// 2026-08-12 | AI 线上面谈 | 10:00~11:00
const formatMeeting = (option: CounselingBooking) => {
  const [date, start = ''] = (option.startAt || '').split(' ')
  const end = (option.endAt || '').split(' ')[1] || ''
  const type = option.counselingMode === CounselingMode.AI ? t('employee.meeting.aiOnlineInterview') : option.typeName
  return `${date} | ${type || '-'} | ${start.slice(0, 5)}~${end.slice(0, 5)}`
}

onMounted(async () => {
  loading.value = true
  try {
    // 按面谈时间由近及远，默认选中最近一次
    const list = (await getRecommendReasonInterviewsApi(props.candidateId, props.jobId)) || []
    meetingOptions.value = [...list].sort((a, b) => (b.startAt || '').localeCompare(a.startAt || ''))
    formData.bookingId = meetingOptions.value[0]?.bookingId
  } catch (error) {
    console.error('获取面谈纪要失败:', error)
  } finally {
    loading.value = false
  }
})

const handleConfirm = async () => {
  try {
    await formRef.value?.validate()
  } catch {
    return
  }
  emit('confirm', formData.bookingId!)
}
</script>

<style lang="scss">
.ai-recommend-reason-dialog {
  width: 560px;

  .ai-reason-form {
    .el-form-item__label {
      font-size: 14px;
      font-weight: 500;
      color: #212429;
    }

    .el-select {
      width: 100%;
    }
  }

  .ai-reason-tip {
    margin-top: 8px;
    font-size: 14px;
    line-height: 20px;
    color: #86909C;
  }
}
</style>
